/**
 * Furniture sprite loader
 * Loads furniture sprites based on the catalog and layout data
 */

import type { LayoutData } from "../types/index.js";
import catalogData from "../assets/furniture/furniture-catalog.json";

export interface FurnitureAsset {
  id: string;
  name: string;
  file: string;
  width: number;
  height: number;
  footprintW: number;
  footprintH: number;
  backgroundTiles?: number;
}

export interface FurniturePlacement {
  uid: string;
  type: string;
  col: number;
  row: number;
  asset: FurnitureAsset | null;
  image: HTMLImageElement | null;
}

// Build asset lookup from catalog
const assetCatalog = new Map<string, FurnitureAsset>();
for (const asset of (catalogData as any).assets) {
  assetCatalog.set(asset.id, {
    id: asset.id,
    name: asset.name,
    file: asset.file,
    width: asset.width,
    height: asset.height,
    footprintW: asset.footprintW,
    footprintH: asset.footprintH,
    backgroundTiles: asset.backgroundTiles,
  });
}

// Image cache
const imageCache = new Map<string, HTMLImageElement>();
const loadingPromises = new Map<string, Promise<HTMLImageElement | null>>();

/**
 * Load a furniture image by asset ID
 */
export async function loadFurnitureImage(assetId: string): Promise<HTMLImageElement | null> {
  // Check cache
  if (imageCache.has(assetId)) {
    return imageCache.get(assetId)!;
  }

  // Check if already loading
  if (loadingPromises.has(assetId)) {
    return loadingPromises.get(assetId)!;
  }

  const asset = assetCatalog.get(assetId);
  if (!asset) {
    console.warn(`[FurnitureLoader] Unknown asset: ${assetId}`);
    return null;
  }

  const promise = new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    img.onload = () => {
      imageCache.set(assetId, img);
      loadingPromises.delete(assetId);
      resolve(img);
    };
    img.onerror = () => {
      console.warn(`[FurnitureLoader] Failed to load: ${asset.file}`);
      loadingPromises.delete(assetId);
      resolve(null);
    };
    // Path relative to the public assets folder - use Vite base URL
    const baseUrl = import.meta.env.BASE_URL || "./";
    img.src = `${baseUrl}assets/${asset.file}`;
  });

  loadingPromises.set(assetId, promise);
  return promise;
}

/**
 * Get furniture placements from layout data with loaded images
 */
export function getFurniturePlacements(layout: LayoutData): FurniturePlacement[] {
  const furniture = (layout as any).furniture || [];
  return furniture.map((f: any) => {
    const asset = assetCatalog.get(f.type) || null;
    return {
      uid: f.uid,
      type: f.type,
      col: f.col,
      row: f.row,
      asset,
      image: asset ? imageCache.get(f.type) || null : null,
    };
  });
}

/**
 * Preload all furniture images from layout
 */
export async function preloadFurnitureImages(layout: LayoutData): Promise<void> {
  const furniture = (layout as any).furniture || [];
  const assetIds = new Set<string>();

  for (const f of furniture) {
    assetIds.add(f.type);
  }

  await Promise.all(Array.from(assetIds).map((id) => loadFurnitureImage(id)));
}

/**
 * Get asset info by ID
 */
export function getAssetInfo(assetId: string): FurnitureAsset | null {
  return assetCatalog.get(assetId) || null;
}

/**
 * Check if an image is loaded for an asset
 */
export function isAssetLoaded(assetId: string): boolean {
  return imageCache.has(assetId);
}

/**
 * Get cached image for an asset (or null if not loaded)
 */
export function getCachedImage(assetId: string): HTMLImageElement | null {
  return imageCache.get(assetId) || null;
}
